import Link from "next/link";

interface NavItem {
  href: string;
  title: string;
}

interface PrevNextNavProps {
  prev: NavItem | null;
  next: NavItem | null;
  basePath: string;
  baseLabel: string;
}

export default function PrevNextNav({ prev, next, basePath, baseLabel }: PrevNextNavProps) {
  return (
    <section className="py-8 px-6 border-t border-[#1a1a1a]/10">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-3 items-center">
          {/* Previous */}
          <div className="justify-self-start">
            {prev ? (
              <Link
                href={prev.href}
                className="group flex items-center gap-3 py-4 pr-6 hover:text-[#ff6900] transition-colors"
              >
                <svg
                  className="w-5 h-5 text-[#1a1a1a]/30 group-hover:text-[#ff6900] group-hover:-translate-x-1 transition-all"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <div className="text-left">
                  <span className="text-xs text-[#1a1a1a]/70 uppercase tracking-wider block mb-1">Previous</span>
                  <span className="font-medium">{prev.title}</span>
                </div>
              </Link>
            ) : null}
          </div>

          {/* Center - Back to listing */}
          <div className="justify-self-center">
            <Link
              href={basePath}
              className="hidden md:flex items-center gap-2 px-4 py-2 text-sm text-[#1a1a1a]/70 hover:text-[#ff6900] transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              {baseLabel}
            </Link>
          </div>

          {/* Next */}
          <div className="justify-self-end">
            {next ? (
              <Link
                href={next.href}
                className="group flex items-center gap-3 py-4 pl-6 hover:text-[#ff6900] transition-colors"
              >
                <div className="text-right">
                  <span className="text-xs text-[#1a1a1a]/70 uppercase tracking-wider block mb-1">Next</span>
                  <span className="font-medium">{next.title}</span>
                </div>
                <svg
                  className="w-5 h-5 text-[#1a1a1a]/30 group-hover:text-[#ff6900] group-hover:translate-x-1 transition-all"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

// Service pages navigation data (matches landing page order)
export const servicePages = [
  { href: "/services/full-stack-web-development", title: "AI-Native Software Delivery" },
  { href: "/services/data-services", title: "Data Services" },
  { href: "/services/ux-ui-design", title: "UX/UI Design" },
  { href: "/services/content-management", title: "Content Management" },
  { href: "/services/mobile-app-development", title: "Mobile App Development" },
  { href: "/services/ai-ml", title: "AI & Machine Learning" },
];

// All project pages - unified navigation for work and success stories (matches landing page order)
export const allProjectPages = [
  // Featured Work
  { href: "/work/aspen-one", title: "Aspen One" },
  { href: "/work/simplot", title: "J.R. Simplot Company" },
  { href: "/work/alterra-mountain-company", title: "Alterra Mountain Company" },
  { href: "/work/portal-thermaculture", title: "Portal Thermaculture" },
  { href: "/work/mcdonalds-wwc", title: "McDonald's Potato Supply Chain Tracker" },
  { href: "/work/truscore", title: "TruScore" },
  // Success Stories
  { href: "/success-story/evolve-vacation-rentals", title: "Evolve Vacation Rentals" },
  { href: "/success-story/guidehouse", title: "Guidehouse" },
  { href: "/success-story/hexa-custom", title: "Hexa Custom" },
  { href: "/success-story/spirent", title: "Spirent" },
  { href: "/success-story/pinnacle-agriculture", title: "Pinnacle Agriculture" },
  { href: "/success-story/jackson-hole", title: "Jackson Hole" },
  { href: "/success-story/frontier-airlines", title: "Frontier Airlines" },
  { href: "/success-story/evans-senior-investments", title: "Evans Senior Investments" },
  { href: "/success-story/vail-resorts", title: "Vail Resorts" },
];

// Legacy exports for backwards compatibility
export const workPages = allProjectPages.filter(p => p.href.startsWith("/work/"));
export const successStoryPages = allProjectPages.filter(p => p.href.startsWith("/success-story/"));

export function getServiceNav(currentHref: string) {
  const currentIndex = servicePages.findIndex((p) => p.href === currentHref);
  return {
    prev: currentIndex > 0 ? servicePages[currentIndex - 1] : servicePages[servicePages.length - 1],
    next: currentIndex < servicePages.length - 1 ? servicePages[currentIndex + 1] : servicePages[0],
  };
}

// Unified navigation for all project pages (work + success stories)
export function getProjectNav(currentHref: string) {
  const currentIndex = allProjectPages.findIndex((p) => p.href === currentHref);
  return {
    prev: currentIndex > 0 ? allProjectPages[currentIndex - 1] : allProjectPages[allProjectPages.length - 1],
    next: currentIndex < allProjectPages.length - 1 ? allProjectPages[currentIndex + 1] : allProjectPages[0],
  };
}

// Legacy functions - now use unified navigation
export function getWorkNav(currentHref: string) {
  return getProjectNav(currentHref);
}

export function getSuccessStoryNav(currentHref: string) {
  return getProjectNav(currentHref);
}
